'use client';

import { useEffect, useMemo, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  NAME_VARIANT_ROLE_LABELS,
  RECONCILIATION_BASIS_LABELS,
  type CreateNameVariantRequest,
  type NameVariant,
  type NameVariantRole,
  type ReconciliationBasis
} from '@/lib/services/identity-variants';
import {
  ATTRIBUTE_COMPOSITE_PART,
  ATTRIBUTE_COMPOSITE_PART_CONTROL,
  ATTRIBUTE_COMPOSITE_PART_LABEL,
  ATTRIBUTE_LABEL_TEXT,
  ATTRIBUTE_MUTED_INK
} from '@/components/attributes';
import { cn } from '@/lib/utils';
import { VariantInlineEditor } from './variant-inline-editor';

/**
 * Inline add / edit for one of the other names a person is known by
 * (LF-5566 PROV-21; moved inline in LF-5714).
 *
 * The role and reconciliation basis are not decoration — they drive the
 * court-form alias recital: `formal_change_with_evidence` makes Vale flag that
 * evidence of the change of name must be annexed, while an informal alias or
 * spelling variant is recited without annexures.
 *
 * Mounted fresh per row (the section keys it by variant id), so the initial
 * state below is the whole reset story — no open/close resync needed.
 */

const NO_BASIS = 'none';

interface NameVariantFormProps {
  /** Absent when adding. */
  variant?: NameVariant;
  onSubmit: (data: CreateNameVariantRequest) => Promise<unknown>;
  onCancel: () => void;
  isSubmitting?: boolean;
  /** Passed through to the shell — see `VariantInlineEditor`'s own prop. */
  bare?: boolean;
  /**
   * **Commit-on-dismiss instead of a Save button** (VALE-863).
   *
   * The footer disappears and the host commits whatever the form last reported
   * through {@link onDraftChange}. That is the contract every other editor on
   * this treatment already keeps — a `name` or `address` popover commits when
   * it is dismissed and reverts on `Escape` — so a variant editor asking for a
   * Save click was the odd one out.
   */
  autoSave?: boolean;
  /**
   * Reports the payload this form would submit, or `null` while it is not
   * valid to submit. Only meaningful with {@link autoSave}; the host holds the
   * latest value and sends it when the popover closes.
   */
  onDraftChange?: (payload: CreateNameVariantRequest | null) => void;
}

export function NameVariantForm({
  variant,
  onSubmit,
  onCancel,
  isSubmitting = false,
  bare,
  autoSave,
  onDraftChange
}: NameVariantFormProps) {
  const isEdit = !!variant;

  const [value, setValue] = useState(variant?.value ?? '');
  const [role, setRole] = useState<NameVariantRole>(
    variant?.role ?? 'other_aka'
  );
  const [isCurrent, setIsCurrent] = useState(variant?.isCurrent ?? true);
  const [basis, setBasis] = useState<string>(
    variant?.reconciliationBasis ?? NO_BASIS
  );

  const trimmed = value.trim();
  const payload: CreateNameVariantRequest | null = useMemo(
    () =>
      trimmed
        ? {
            value: trimmed,
            isPrimary: false,
            role,
            isCurrent,
            reconciliationBasis:
              basis === NO_BASIS ? null : (basis as ReconciliationBasis)
          }
        : null,
    [trimmed, role, isCurrent, basis]
  );

  /*
    Reported on every keystroke rather than on blur: the host commits when the
    popover is dismissed, and a dismissal can happen from a click anywhere on
    the page — there is no blur to wait for that is guaranteed to land first.
  */
  useEffect(() => {
    onDraftChange?.(payload);
  }, [payload, onDraftChange]);

  const handleSubmit = async () => {
    if (!payload) return;

    /*
      `isPrimary: false` always — that flag marks the canonical name, which
      lives on the owner's own name fields; a row added here is by definition
      not that, and the list filters primary rows out. Set where `payload` is
      built, so the auto-save path cannot diverge from this one.
    */
    await onSubmit(payload);
    onCancel();
  };

  return (
    <VariantInlineEditor
      title={isEdit ? 'Edit name' : 'Add a name'}
      submitLabel={isEdit ? 'Save name' : 'Add name'}
      canSubmit={!!payload}
      isSubmitting={isSubmitting}
      onSubmit={handleSubmit}
      onCancel={onCancel}
      bare={bare}
      autoSave={autoSave}
    >
      <div className={ATTRIBUTE_COMPOSITE_PART}>
        <Label
          htmlFor="variant-name-value"
          className={ATTRIBUTE_COMPOSITE_PART_LABEL}
        >
          Name *
        </Label>
        <Input
          id="variant-name-value"
          value={value}
          onChange={(event) => setValue(event.target.value)}
          placeholder="e.g. Katherin Citizen"
          autoFocus
          className={ATTRIBUTE_COMPOSITE_PART_CONTROL}
        />
      </div>

      <div className="grid gap-2 sm:grid-cols-2">
        <div className={ATTRIBUTE_COMPOSITE_PART}>
          <Label
            htmlFor="variant-name-role"
            className={ATTRIBUTE_COMPOSITE_PART_LABEL}
          >
            How this name is used *
          </Label>
          <Select
            value={role}
            onValueChange={(next) => setRole(next as NameVariantRole)}
          >
            <SelectTrigger
              id="variant-name-role"
              className={ATTRIBUTE_COMPOSITE_PART_CONTROL}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(NAME_VARIANT_ROLE_LABELS).map(([key, label]) => (
                <SelectItem key={key} value={key}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className={ATTRIBUTE_COMPOSITE_PART}>
          <Label
            htmlFor="variant-name-basis"
            className={ATTRIBUTE_COMPOSITE_PART_LABEL}
          >
            Basis for reconciling
          </Label>
          <Select value={basis} onValueChange={setBasis}>
            <SelectTrigger
              id="variant-name-basis"
              className={ATTRIBUTE_COMPOSITE_PART_CONTROL}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_BASIS}>Not specified</SelectItem>
              {Object.entries(RECONCILIATION_BASIS_LABELS).map(
                ([key, label]) => (
                  <SelectItem key={key} value={key}>
                    {label}
                  </SelectItem>
                )
              )}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex items-center space-x-2">
        <Checkbox
          id="variant-name-current"
          checked={isCurrent}
          onCheckedChange={(checked) => setIsCurrent(checked === true)}
        />
        <Label
          htmlFor="variant-name-current"
          className={cn(ATTRIBUTE_COMPOSITE_PART_LABEL, 'font-normal')}
        >
          Still goes by this name
        </Label>
      </div>

      <p className={cn(ATTRIBUTE_LABEL_TEXT, ATTRIBUTE_MUTED_INK)}>
        A formal change flags that evidence (a marriage certificate or deed
        poll) must be annexed to the affidavit.
      </p>
    </VariantInlineEditor>
  );
}
